import { Button, Loader } from '@trackman/web-shared-components';
import { ActionButton } from 'components/ActionButton/ActionButton';
import { ReactComponent as CloseIcon } from 'images/Review/TopBar/closeIcon.svg';
import { ReactComponent as SettingsIcon } from 'images/Review/TopBar/settingsIcon.svg';
import { ISessionUpdateRequest } from 'interfaces/review/session/Session';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { ITeam } from 'interfaces/team/ITeam';
import { observer } from 'mobx-react-lite';
import { SessionReviewRowManager } from 'models/review/SessionReviewRowManager';
import React from 'react';
import { useHistory } from 'react-router-dom';
import EditSessionModal from '../EditSessionModal/EditSessionModal';
import { WarmupToggle } from '../WarmupToggle/WarmupToggle';
import { Controls, Divider, LeftSide, TaggingInfo, Teams, TopBarWrapper, Versus } from './TopBarStyles';

interface IProps {
  homeTeam: ITeam | null;
  awayTeam: ITeam | null;
  taggedDate: string;
  stadium: string;
  onSave: () => void;
  onSubmitClickHandler: () => void;
  areUnsavedChanges: boolean;
  isSaving: boolean;
  isLoading: boolean;
  shouldDisplayWarmups: boolean;
  shouldDisplayWarmupsCallback: (checked: boolean) => void;
  isBattingPractice?: boolean;
  shouldUpdate?: string;
  gameRef?: string;
  sessionMetadata: IApiSessionMetadataUpdated;
  onSessionUpdated?: (newSession: ISessionUpdateRequest) => void;
  rowManager: SessionReviewRowManager;
  isSettingsModalVisible: boolean;
  setSettingsModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const TopBar: React.FC<IProps> = observer((props) => {
  const {
    homeTeam,
    awayTeam,
    taggedDate,
    stadium,
    onSave,
    areUnsavedChanges,
    isSaving,
    onSubmitClickHandler,
    isLoading,
    shouldDisplayWarmups,
    shouldDisplayWarmupsCallback,
    isBattingPractice,
    gameRef,
    sessionMetadata,
    onSessionUpdated,
    rowManager,
    isSettingsModalVisible,
    setSettingsModalVisible,
  } = props;
  const isSaveDisabled = isSaving;
  const history = useHistory();

  const updateSessionMetadata = async (updateRequest: ISessionUpdateRequest) => {
    if (onSessionUpdated) {
      onSessionUpdated(updateRequest);
    }
  };

  return (
    <TopBarWrapper>
      <div>
        {awayTeam !== undefined && homeTeam !== undefined && (
          <LeftSide>
            <ActionButton
              icon={<CloseIcon />}
              onClick={() => {
                history.push('/archives');
              }}
              title='Close'
            />
            <div>
              {isBattingPractice ? (
                <Teams>Batting Practice: {homeTeam?.name}</Teams>
              ) : (
                <Teams>
                  {awayTeam?.name}
                  <Versus> vs </Versus>
                  {homeTeam?.name}
                </Teams>
              )}
              <TaggingInfo>
                Tagged on {taggedDate} @ {stadium} {gameRef && `- ${gameRef}`}
              </TaggingInfo>
            </div>
          </LeftSide>
        )}
      </div>
      <Controls>
        <WarmupToggle
          shouldDisplayWarmups={shouldDisplayWarmups}
          shouldDisplayWarmupsCallback={shouldDisplayWarmupsCallback}
          disabled={false}
        />
        {isSaving ? <Loader /> : null}
        <ActionButton title='Settings' icon={<SettingsIcon />} onClick={() => setSettingsModalVisible(true)} />
        <Divider />
        <Button
          label='Save draft'
          intent='tertiary'
          onClick={() => !isSaveDisabled && onSave()}
          data-testid='save-session'
          style={{ backgroundColor: '#f5f5f5', color: '#414141' }}
          width={99}
        />
        <div>
          {isLoading ? (
            <Loader />
          ) : (
            <Button
              intent='primary'
              label='submit'
              onClick={onSubmitClickHandler}
              width={75}
              disabled={isLoading || isSaving}
              data-test='submit-session'
            />
          )}
        </div>
      </Controls>
      <EditSessionModal
        onApplyCallback={(updateRequest: ISessionUpdateRequest) => {
          updateSessionMetadata(updateRequest);
          setSettingsModalVisible(false);
        }}
        visible={isSettingsModalVisible}
        onDismissCallback={() => setSettingsModalVisible(false)}
        session={sessionMetadata}
        contextualDataLoading={false}
      />
    </TopBarWrapper>
  );
});

export default TopBar;
